import type { View } from 'react-native';
import type { StoreApi } from 'zustand';

import type { ReplyDraftTourActions } from './replyDraftTourContext';
import { tourZoneKey, type ReplyDraftTourSegment } from '../../model/tour';
import { neighbourZone, type ReplyDraftTourStore } from '../../state/replyDraftTourStore';

import type { TourCalloutHandles } from '@/hooks/useTourCallout';
import {
  trackReplyDraftTourCompleted,
  trackReplyDraftTourSkipped,
  trackReplyDraftTourStarted,
} from '@/util/analyticsEvents';

type TourIds = { replyDraftId: string; conversationId: string };

/** Scrolls the wizard so a target is in sight, resolving once it has settled. */
export type RevealTarget = (node: View) => Promise<void>;

/**
 * Builds the tour's actions once, over its store and the latest callout
 * handles and draft ids, so every action keeps one identity for the life of
 * the wizard.
 *
 * Only a first-draft run touches the callout: `shown` when its first segment
 * reaches the screen, and `resolve` on Skip or on the last step's Done. A
 * replay writes nothing. Closing the wizard mid-tour resolves nothing, so the
 * tour comes back on the next draft.
 *
 * Only the press that finishes the current segment counts. A late press on a
 * tooltip still fading out, after Skip or Done has already ended the tour,
 * would otherwise record a second completion.
 *
 * The tour measures a target where it sits and never scrolls to it, so every
 * move scrolls the next target into sight first, through `revealTarget`, which
 * the wizard supplies because only it owns the scroll view.
 */
export function createReplyDraftTourActions(
  store: StoreApi<ReplyDraftTourStore>,
  callout: { current: TourCalloutHandles },
  ids: { current: TourIds },
  revealTarget: { current: RevealTarget | undefined }
): ReplyDraftTourActions {
  const isFirstDraftRun = () => store.getState().trigger === 'auto';

  const zoneNodes: Record<string, View | null> = {};

  const reveal = async (segment: ReplyDraftTourSegment, zone: number | null) => {
    const node = zone === null ? null : zoneNodes[tourZoneKey(segment, zone)];

    if (node && revealTarget.current) {
      await revealTarget.current(node);
    }
  };

  return {
    start: (step) => {
      if (callout.current.shouldShow) {
        store.getState().begin('auto', step);
      }
    },

    replay: (step) => {
      store.getState().begin('replay', step);
    },

    advance: ({ segment, zone, isLastStep, handleNext, handleStop }) => {
      if (!isLastStep) {
        reveal(segment, neighbourZone(store.getState(), segment, zone, 1))
          .catch(() => undefined)
          .finally(handleNext);

        return;
      }

      handleStop();

      if (store.getState().phase !== segment) {
        return;
      }

      if (store.getState().finishSegment(segment) !== 'done') {
        return;
      }

      if (isFirstDraftRun()) {
        callout.current.markSeen();
      }

      trackReplyDraftTourCompleted({ ...ids.current, trigger: store.getState().trigger });
    },

    back: (segment, zone, handlePrev) => {
      reveal(segment, neighbourZone(store.getState(), segment, zone, -1))
        .catch(() => undefined)
        .finally(handlePrev);
    },

    setZoneNode: (key, node) => {
      zoneNodes[key] = node;
    },

    reveal,

    skip: (segment, zone, handleStop) => {
      handleStop();

      store.getState().skip();

      if (isFirstDraftRun()) {
        callout.current.markSeen();
      }

      trackReplyDraftTourSkipped({
        ...ids.current,
        trigger: store.getState().trigger,
        segment,
        tourStep: zone,
      });
    },

    segmentShown: (segment) => {
      if (!store.getState().segmentStarted(segment)) {
        return;
      }

      if (isFirstDraftRun()) {
        callout.current.markShown();
      }

      trackReplyDraftTourStarted({ ...ids.current, trigger: store.getState().trigger, segment });
    },
  };
}
